import {
  AlertTriangle,
  Banknote,
  BriefcaseBusiness,
  CalendarClock,
  CalendarRange,
  Clock3,
  History,
  Plus,
  RotateCcw,
  Save,
  Scale,
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import api from "../api/client";
import { ErrorNotice, LoadingState, SuccessNotice } from "../components/Feedback";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import type {
  AccrualPolicy,
  BalanceLayer,
  BalanceMutation,
  BalanceSummary,
  EmploymentContract,
  EmploymentRelationship,
  LeaveType,
  OrganizationMember,
  Paginated,
} from "../types/api";
import { unwrapResults } from "../types/api";
import { apiErrorMessage, hasUncertainApiOutcome } from "../utils/apiError";
import { formatDate, formatMinutes } from "../utils/format";
import DateField from "../components/DateField";

type Section = "overview" | "rules" | "mutations";
type BalanceResponse = {
  year: number;
  user: number;
  organization: number | null;
  employment: number | null;
  period?: {
    mode: "calendar" | "contract";
    label: string;
    start_date: string;
    end_date: string;
    as_of: string;
    contract: number | null;
  };
  summary: BalanceSummary[];
  layers: BalanceLayer[];
  unpaid_leave?: UnpaidLeaveTotals;
};

// Onbetaald verlof heeft geen saldo; alleen de ingeplande omvang is relevant.
type UnpaidLeaveTotals = {
  approved_minutes: number;
  requested_minutes: number;
  planned_minutes: number;
  total_minutes: number;
  leave_type_ids: number[];
};

const today = new Date().toISOString().slice(0, 10);
const currentYear = new Date().getFullYear();

export default function BalancePage() {
  const { user } = useAuth();
  const { organizationId, organization, capabilities } = useWorkspace();
  const [section, setSection] = useState<Section>("overview");
  const [targetUser, setTargetUser] = useState<number | null>(user?.id ?? null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [employments, setEmployments] = useState<EmploymentRelationship[]>([]);
  const [employmentId, setEmploymentId] = useState<number | null>(null);
  const [periodMode, setPeriodMode] = useState<"calendar" | "contract">("calendar");
  const [year, setYear] = useState(currentYear);
  const [contractId, setContractId] = useState<number | null>(null);
  const [asOf, setAsOf] = useState(today);
  const [period, setPeriod] = useState<BalanceResponse["period"]>();
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [balances, setBalances] = useState<BalanceSummary[]>([]);
  const [layers, setLayers] = useState<BalanceLayer[]>([]);
  const [unpaidLeave, setUnpaidLeave] = useState<UnpaidLeaveTotals | null>(null);
  const [policies, setPolicies] = useState<AccrualPolicy[]>([]);
  const [mutations, setMutations] = useState<BalanceMutation[]>([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const canManage =
    !organizationId ||
    (capabilities.canManageOrganizationPolicies && !capabilities.organizationReadOnly);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (organizationId) params.set("organization", String(organizationId));
    if (targetUser) params.set("user", String(targetUser));
    if (!organizationId && employmentId) {
      params.set("employment", String(employmentId));
    }
    return params.toString();
  }, [employmentId, organizationId, targetUser]);

  const employmentQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (organizationId) params.set("organization", String(organizationId));
    if (targetUser) params.set("user", String(targetUser));
    return params.toString();
  }, [organizationId, targetUser]);

  const balanceQuery = useMemo(() => {
    const params = new URLSearchParams(query);
    params.set("period", periodMode);
    params.set("year", String(year));
    params.set("as_of", asOf);
    if (periodMode === "contract" && contractId) {
      params.set("contract", String(contractId));
    }
    return params.toString();
  }, [asOf, contractId, periodMode, query, year]);

  useEffect(() => {
    setTargetUser(user?.id ?? null);
    setMembers([]);
    setEmployments([]);
    setEmploymentId(null);
    setPeriodMode("calendar");
  }, [organizationId, user?.id]);

  useEffect(() => {
    if (!organizationId || !capabilities.canManageOrganizationPolicies) return;
    void api
      .get<OrganizationMember[]>(`/organizations/${organizationId}/members/`)
      .then((response) =>
        setMembers(response.data.filter((row) => row.status === "active")),
      )
      .catch(() => setError("De medewerkers konden niet worden geladen."));
  }, [organizationId, capabilities.canManageOrganizationPolicies]);

  useEffect(() => {
    if (!targetUser) return;
    const suffix = employmentQuery ? `?${employmentQuery}` : "";
    void api
      .get<Paginated<EmploymentRelationship> | EmploymentRelationship[]>(
        `/organizations/employments/${suffix}`,
      )
      .then((response) => {
        const rows = unwrapResults(response.data);
        setEmployments(rows);
        const active = rows.find((employment) =>
          employment.contracts.some(
            (contract) =>
              contract.start_date <= today &&
              (!contract.end_date || contract.end_date >= today),
          ),
        );
        const selected = active ?? rows[0];
        setEmploymentId(selected?.id ?? null);
        const fixedContract = selected?.contracts.find(
          (contract) => contract.contract_type === "fixed_term",
        );
        setContractId(fixedContract?.id ?? null);
        if (!fixedContract) setPeriodMode("calendar");
      })
      .catch(() => setError("De werkgevers en contracten konden niet worden geladen."));
  }, [employmentQuery, targetUser]);

  const load = useCallback(async (): Promise<BalanceMutation[] | null> => {
    if (!targetUser) return null;
    setLoading(true);
    setError("");
    const suffix = query ? `?${query}` : "";
    const balanceSuffix = `?${balanceQuery}`;
    const organizationSuffix = organizationId ? `?organization=${organizationId}` : "";
    try {
      const [
        typesResponse,
        balanceResponse,
        policyResponse,
        mutationResponse,
      ] = await Promise.all([
        api.get<Paginated<LeaveType> | LeaveType[]>(
          `/leave/types/${organizationSuffix}`,
        ),
        api.get<BalanceResponse>(`/leave/balances/overview/${balanceSuffix}`),
        api.get<Paginated<AccrualPolicy> | AccrualPolicy[]>(
          `/leave/accrual-policies/${suffix}`,
        ),
        api.get<Paginated<BalanceMutation> | BalanceMutation[]>(
          `/leave/mutations/${suffix}`,
        ),
      ]);
      setLeaveTypes(unwrapResults(typesResponse.data));
      setBalances(balanceResponse.data.summary);
      setLayers(balanceResponse.data.layers);
      setUnpaidLeave(balanceResponse.data.unpaid_leave ?? null);
      setPeriod(balanceResponse.data.period);
      setPolicies(unwrapResults(policyResponse.data));
      const nextMutations = unwrapResults(mutationResponse.data);
      setMutations(nextMutations);
      return nextMutations;
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De saldogegevens konden niet worden geladen."),
      );
      return null;
    } finally {
      setLoading(false);
    }
  }, [balanceQuery, organizationId, query, targetUser]);

  useEffect(() => {
    void load();
  }, [load]);

  const regulatedTypes = useMemo(
    () =>
      leaveTypes.filter(
        (leaveType) =>
          leaveType.requires_balance &&
          !["overtime", "time_for_time"].includes(leaveType.category),
      ),
    [leaveTypes],
  );

  const selectedEmployment =
    employments.find((employment) => employment.id === employmentId) ?? employments[0];
  const fixedContracts: EmploymentContract[] =
    selectedEmployment?.contracts.filter(
      (contract) => contract.contract_type === "fixed_term",
    ) ?? [];
  const selectedContract = fixedContracts.find(
    (contract) => contract.id === contractId,
  );

  const chooseEmployment = (nextId: number) => {
    setEmploymentId(nextId);
    const employment = employments.find((row) => row.id === nextId);
    const fixedContract = employment?.contracts.find(
      (contract) => contract.contract_type === "fixed_term",
    );
    setContractId(fixedContract?.id ?? null);
    if (!fixedContract) setPeriodMode("calendar");
  };

  const selectedMember = members.find((member) => member.user === targetUser);
  const targetName =
    selectedMember?.display_name ?? (targetUser === user?.id ? "jouw" : "het");
  const totalCurrent = balances.reduce((sum, row) => sum + row.current_minutes, 0);
  const totalAvailable = balances.reduce((sum, row) => sum + row.available_minutes, 0);
  const layerReference = period
    ? new Date(`${period.as_of}T12:00:00`).getTime()
    : Date.now();
  const expiringSoon = layers.filter((layer) => {
    const days =
      (new Date(`${layer.expires_on}T12:00:00`).getTime() - layerReference) /
      86_400_000;
    return layer.remaining_minutes > 0 && days >= 0 && days <= 90;
  });

  if (loading) return <LoadingState label="Saldo laden..." />;

  return (
    <div className="balance-page">
      <PageHeader
        eyebrow={organization?.name ?? "Persoonlijk"}
        title="Saldo"
        description={`Bekijk ${targetName} opbouw, vervaldatums en alle boekingen.`}
        actions={
          <div className="page-header-actions">
            <Link className="button subtle" to="/app/requests/new?type=overtime">
              <Clock3 size={17} /> Overuren registreren
            </Link>
            <Link className="button primary" to="/app/requests/new?type=time_for_time">
              <CalendarClock size={17} /> Tijd voor tijd opnemen
            </Link>
          </div>
        }
      />

      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}

      {organizationId &&
        capabilities.canManageOrganizationPolicies &&
        members.length > 0 && (
          <label className="employee-picker">
            <span>Medewerker</span>
            <select
              value={targetUser ?? ""}
              onChange={(event) => {
                setTargetUser(Number(event.target.value));
                setEmploymentId(null);
                setPeriodMode("calendar");
              }}
            >
              {members.map((member) => (
                <option key={member.id} value={member.user}>
                  {member.display_name} ({member.role.replace("org_admin", "beheerder")}
                  )
                </option>
              ))}
            </select>
          </label>
        )}

      <section className="surface balance-period-toolbar" aria-label="Saldoperiode">
        {employments.length > 0 && (
          <label>
            <span>Werkgever</span>
            <select
              value={selectedEmployment?.id ?? ""}
              onChange={(event) => chooseEmployment(Number(event.target.value))}
            >
              {employments.map((employment) => (
                <option key={employment.id} value={employment.id}>
                  {employment.display_name}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="period-mode-field">
          <span>Periode</span>
          <div className="segmented-control" role="group" aria-label="Periodesoort">
            <button
              type="button"
              className={periodMode === "calendar" ? "active" : ""}
              onClick={() => {
                setPeriodMode("calendar");
                setAsOf(year === currentYear ? today : `${year}-12-31`);
              }}
            >
              <CalendarRange size={16} /> Kalenderjaar
            </button>
            <button
              type="button"
              className={periodMode === "contract" ? "active" : ""}
              disabled={fixedContracts.length === 0}
              onClick={() => {
                setPeriodMode("contract");
                if (!contractId && fixedContracts[0]) {
                  setContractId(fixedContracts[0].id);
                }
                const contract =
                  fixedContracts.find((row) => row.id === contractId) ??
                  fixedContracts[0];
                if (contract?.end_date) {
                  setAsOf(
                    today < contract.start_date
                      ? contract.start_date
                      : today > contract.end_date
                      ? contract.end_date
                      : today,
                  );
                }
              }}
            >
              <BriefcaseBusiness size={16} /> Contractduur
            </button>
          </div>
        </div>
        {periodMode === "calendar" ? (
          <label>
            <span>Jaar</span>
            <input
              type="number"
              min="2000"
              max="2200"
              value={year}
              onChange={(event) => {
                const nextYear = Number(event.target.value);
                setYear(nextYear);
                setAsOf(nextYear === currentYear ? today : `${nextYear}-12-31`);
              }}
            />
          </label>
        ) : (
          <label>
            <span>Tijdelijk contract</span>
            <select
              value={contractId ?? ""}
              onChange={(event) => {
                const nextId = Number(event.target.value);
                setContractId(nextId);
                const contract = fixedContracts.find((row) => row.id === nextId);
                if (contract?.end_date) {
                  setAsOf(
                    today < contract.start_date
                      ? contract.start_date
                      : today > contract.end_date
                      ? contract.end_date
                      : today,
                  );
                }
              }}
            >
              {fixedContracts.map((contract) => (
                <option key={contract.id} value={contract.id}>
                  {contract.label ||
                    `${formatDate(contract.start_date)} - ${formatDate(
                      contract.end_date ?? contract.start_date,
                    )}`}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          <span>Peildatum</span>
          <DateField
            min={
              periodMode === "contract" && selectedContract
                ? selectedContract.start_date
                : `${year}-01-01`
            }
            max={
              periodMode === "contract" && selectedContract?.end_date
                ? selectedContract.end_date
                : `${year}-12-31`
            }
            value={asOf}
            onChange={(value) => setAsOf(value)}
          />
        </label>
        {period && (
          <span className="period-caption">
            {formatDate(period.start_date)} t/m {formatDate(period.end_date)}
          </span>
        )}
      </section>

      <div
        className="section-tabs balance-tabs"
        role="tablist"
        aria-label="Saldoweergave"
      >
        <button
          className={section === "overview" ? "active" : ""}
          onClick={() => setSection("overview")}
        >
          <Scale size={17} /> Overzicht
        </button>
        <button
          className={section === "rules" ? "active" : ""}
          onClick={() => setSection("rules")}
        >
          <CalendarClock size={17} /> Regelingen
        </button>
        <button
          className={section === "mutations" ? "active" : ""}
          onClick={() => setSection("mutations")}
        >
          <History size={17} /> Mutaties
        </button>
      </div>

      {section === "overview" && (
        <BalanceOverview
          balances={balances}
          layers={layers}
          totalCurrent={totalCurrent}
          totalAvailable={totalAvailable}
          expiringSoon={expiringSoon}
          period={period}
          unpaidLeave={unpaidLeave}
        />
      )}

      {section === "rules" && (
        <section className="surface balance-rules">
          <div className="balance-section-heading">
            <div>
              <h2>Jaarlijks recht en opbouw</h2>
              <p>
                Deze regelingen worden beheerd bij Werk & verlof en zijn hier alleen ter
                inzage zichtbaar.
              </p>
            </div>
            <Link className="button subtle" to="/app/settings?tab=work">
              Werk & verlof openen
            </Link>
          </div>
          <div className="regulation-list">
            {regulatedTypes.map((leaveType) => {
              const policy = policies.find((row) => row.leave_type === leaveType.id);
              return (
                <article className="regulation-row" key={leaveType.id}>
                  <div className="regulation-name">
                    <strong>{leaveType.name}</strong>
                    <span>{categoryLabel(leaveType.category)}</span>
                  </div>
                  <div className="regulation-readonly-value">
                    <span>Jaarrecht</span>
                    <strong>{policy ? `${policy.annual_hours} uur` : "Niet ingesteld"}</strong>
                  </div>
                  <div className="regulation-readonly-value">
                    <span>Opbouw</span>
                    <strong>
                      {policy
                        ? policy.frequency === "monthly"
                          ? "Maandelijks"
                          : policy.frequency === "manual"
                          ? "Handmatig"
                          : "Jaarlijks"
                        : "Via Werk & verlof"}
                    </strong>
                  </div>
                </article>
              );
            })}
          </div>
          <p className="form-footnote">
            Extra verlofdagen, ziektevrije jaren en uren uit een vorig jaar boek je als
            dag- of urenmutatie op het tabblad Mutaties.
          </p>
        </section>
      )}

      {section === "mutations" && (
        <MutationWorkspace
          canManage={canManage}
          leaveTypes={leaveTypes.filter((row) => row.requires_balance)}
          mutations={mutations}
          organizationId={organizationId}
          targetUser={targetUser}
          query={query}
          working={working}
          setWorking={setWorking}
          setError={setError}
          setSuccess={setSuccess}
          reload={load}
        />
      )}
    </div>
  );
}

function BalanceOverview({
  balances,
  layers,
  totalCurrent,
  totalAvailable,
  expiringSoon,
  period,
  unpaidLeave,
}: {
  balances: BalanceSummary[];
  layers: BalanceLayer[];
  totalCurrent: number;
  totalAvailable: number;
  expiringSoon: BalanceLayer[];
  period: BalanceResponse["period"];
  unpaidLeave: UnpaidLeaveTotals | null;
}) {
  const totalProjected = balances.reduce(
    (sum, row) => sum + (row.projected_end_minutes ?? row.available_minutes),
    0,
  );
  return (
    <>
      <section className="metric-grid balance-metrics">
        <article className="metric-card">
          <span className="metric-icon metric-blue">
            <Scale size={20} />
          </span>
          <div>
            <span>Saldo op peildatum</span>
            <strong>{formatMinutes(totalCurrent)}</strong>
            <small>
              {period ? formatDate(period.as_of) : "Na goedgekeurde boekingen"}
            </small>
          </div>
        </article>
        <article className="metric-card">
          <span className="metric-icon metric-mint">
            <CalendarClock size={20} />
          </span>
          <div>
            <span>Beschikbaar</span>
            <strong>{formatMinutes(totalAvailable)}</strong>
            <small>Na gepland en aangevraagd</small>
          </div>
        </article>
        <article className="metric-card">
          <span className="metric-icon metric-blue">
            <CalendarRange size={20} />
          </span>
          <div>
            <span>Verwacht eindsaldo</span>
            <strong>{formatMinutes(totalProjected)}</strong>
            <small>Met bekende planning en aanvragen</small>
          </div>
        </article>
        <article className="metric-card">
          <span className="metric-icon metric-amber">
            <AlertTriangle size={20} />
          </span>
          <div>
            <span>Vervalt binnen 90 dagen</span>
            <strong>
              {formatMinutes(
                expiringSoon.reduce((sum, row) => sum + row.remaining_minutes, 0),
              )}
            </strong>
            <small>
              {expiringSoon.length} saldolaag{expiringSoon.length === 1 ? "" : "lagen"}
            </small>
          </div>
        </article>
      </section>
      {unpaidLeave && unpaidLeave.total_minutes > 0 && (
        <section className="surface balance-unpaid-leave">
          <div className="balance-section-heading">
            <div>
              <h2>Onbetaald verlof</h2>
              <p>
                Onbetaald verlof heeft geen saldo. Hieronder zie je alleen hoeveel uur
                er in deze periode is vastgelegd.
              </p>
            </div>
            <strong>{formatMinutes(unpaidLeave.total_minutes)}</strong>
          </div>
          <div className="regulation-list">
            <article className="regulation-row">
              <div className="regulation-readonly-value">
                <span>Gepland</span>
                <strong>{formatMinutes(unpaidLeave.planned_minutes)}</strong>
              </div>
              <div className="regulation-readonly-value">
                <span>Aangevraagd</span>
                <strong>{formatMinutes(unpaidLeave.requested_minutes)}</strong>
              </div>
              <div className="regulation-readonly-value">
                <span>Goedgekeurd</span>
                <strong>{formatMinutes(unpaidLeave.approved_minutes)}</strong>
              </div>
            </article>
          </div>
        </section>
      )}
      <section className="surface balance-summary-table">
        <div className="balance-section-heading">
          <div>
            <h2>Saldo per type</h2>
            <p>Gepland en aangevraagd zijn al van beschikbaar afgetrokken.</p>
          </div>
        </div>
        {balances.length === 0 ? (
          <div className="empty-state compact">
            <Scale size={25} />
            <h2>Nog geen saldo</h2>
            <p>Stel een regeling in of voeg een startsaldo toe.</p>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Begin</th>
                  <th>Toegekend</th>
                  <th>Afgeboekt</th>
                  <th>Peildatum</th>
                  <th>Gepland</th>
                  <th>Aangevraagd</th>
                  <th>Verwacht einde</th>
                </tr>
              </thead>
              <tbody>
                {balances.map((row) => (
                  <tr key={row.leave_type_id}>
                    <td>
                      <strong>{row.leave_type}</strong>
                      <small>{categoryLabel(row.category)}</small>
                    </td>
                    <td>{formatMinutes(row.opening_minutes ?? 0)}</td>
                    <td>{formatMinutes(row.credited_minutes ?? 0)}</td>
                    <td>{formatMinutes(row.debited_minutes ?? 0)}</td>
                    <td>{formatMinutes(row.current_minutes)}</td>
                    <td>{formatMinutes(row.planned_minutes)}</td>
                    <td>{formatMinutes(row.requested_minutes)}</td>
                    <td>
                      <strong>
                        {formatMinutes(
                          row.projected_end_minutes ?? row.available_minutes,
                        )}
                      </strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {layers.length > 0 && (
        <section className="surface balance-layer-table">
          <div className="balance-section-heading">
            <div>
              <h2>Herkomst en vervaldatum</h2>
              <p>
                Bij opname worden de uren met de vroegste vervaldatum eerst gebruikt.
              </p>
            </div>
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Bronjaar</th>
                  <th>Resterend</th>
                  <th>Vervalt op</th>
                </tr>
              </thead>
              <tbody>
                {layers
                  .filter((row) => row.remaining_minutes > 0)
                  .map((row) => (
                    <tr key={row.bucket_id}>
                      <td>{row.leave_type}</td>
                      <td>{row.source_year}</td>
                      <td>{formatMinutes(row.remaining_minutes)}</td>
                      <td>{formatDate(row.expires_on)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

function MutationWorkspace({
  canManage,
  leaveTypes,
  mutations,
  organizationId,
  targetUser,
  query,
  working,
  setWorking,
  setError,
  setSuccess,
  reload,
}: {
  canManage: boolean;
  leaveTypes: LeaveType[];
  mutations: BalanceMutation[];
  organizationId: number | null;
  targetUser: number | null;
  query: string;
  working: boolean;
  setWorking: (value: boolean) => void;
  setError: (value: string) => void;
  setSuccess: (value: string) => void;
  reload: () => Promise<BalanceMutation[] | null>;
}) {
  const [kind, setKind] = useState<
    "opening" | "credit" | "correction" | "expiry" | "payout"
  >("credit");
  const [direction, setDirection] = useState<"add" | "subtract">("add");
  const [leaveType, setLeaveType] = useState<number | null>(leaveTypes[0]?.id ?? null);
  const [hours, setHours] = useState("");
  const [adjustmentUnit, setAdjustmentUnit] = useState<"hours" | "days">("hours");
  const [dayHours, setDayHours] = useState("8");
  const [date, setDate] = useState(today);
  const [expiresOn, setExpiresOn] = useState(`${currentYear + 1}-07-01`);
  const [description, setDescription] = useState("");
  const [transportHours, setTransportHours] = useState("");
  const [transportType, setTransportType] = useState<number | null>(
    leaveTypes[0]?.id ?? null,
  );
  const [sourceYear, setSourceYear] = useState(String(currentYear - 1));
  const [transportExpiry, setTransportExpiry] = useState(`${currentYear}-07-01`);

  useEffect(() => {
    if (!leaveTypes.some((row) => row.id === leaveType))
      setLeaveType(leaveTypes[0]?.id ?? null);
    const carryOverTypes = leaveTypes.filter(
      (row) => !["overtime", "time_for_time"].includes(row.category),
    );
    if (!carryOverTypes.some((row) => row.id === transportType)) {
      setTransportType(carryOverTypes[0]?.id ?? null);
    }
  }, [leaveTypes, leaveType, transportType]);

  const submitAdjustment = async (event: FormEvent) => {
    event.preventDefault();
    if (!leaveType) return;
    const subtract =
      kind === "expiry" ||
      kind === "payout" ||
      (kind === "correction" && direction === "subtract");
    const unitMinutes =
      adjustmentUnit === "days"
        ? (Number(dayHours) || 0) * 60
        : 60;
    const minutes =
      Math.round((Number(hours) || 0) * unitMinutes) * (subtract ? -1 : 1);
    const knownMutationIds = new Set(mutations.map((mutation) => mutation.id));
    setWorking(true);
    setError("");
    try {
      await api.post(`/leave/mutations/adjust/${query ? `?${query}` : ""}`, {
        organization: organizationId,
        user: targetUser,
        leave_type: leaveType,
        kind,
        minutes_delta: minutes,
        date,
        expires_on: minutes > 0 ? expiresOn : null,
        description,
      });
      setHours("");
      setDescription("");
      setSuccess("De saldomutatie is geboekt.");
      await reload();
    } catch (requestError: unknown) {
      if (hasUncertainApiOutcome(requestError)) {
        const refreshed = await reload();
        const saved = refreshed?.some(
          (mutation) =>
            !knownMutationIds.has(mutation.id) &&
            mutation.minutes_delta === minutes &&
            mutation.description === description.trim(),
        );
        if (saved) {
          setHours("");
          setDescription("");
          setSuccess("De saldomutatie is geboekt en opnieuw ingelezen.");
          return;
        }
      }
      setError(
        apiErrorMessage(requestError, "De saldomutatie kon niet worden geboekt."),
      );
    } finally {
      setWorking(false);
    }
  };

  const submitTransport = async (event: FormEvent) => {
    event.preventDefault();
    if (!transportType) return;
    setWorking(true);
    setError("");
    try {
      await api.post(`/leave/carry-over/${query ? `?${query}` : ""}`, {
        organization: organizationId,
        user: targetUser,
        leave_type: transportType,
        source_year: Number(sourceYear),
        original_minutes: Math.round((Number(transportHours) || 0) * 60),
        expires_on: transportExpiry,
      });
      setTransportHours("");
      setSuccess("De overjarige uren zijn toegevoegd.");
      await reload();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(
          requestError,
          "De overjarige uren konden niet worden toegevoegd.",
        ),
      );
    } finally {
      setWorking(false);
    }
  };

  const reverse = async (mutation: BalanceMutation) => {
    if (!window.confirm("Deze volledige handmatige boeking tegenboeken?")) return;
    setWorking(true);
    try {
      await api.post(
        `/leave/mutations/${mutation.id}/reverse/${query ? `?${query}` : ""}`,
      );
      setSuccess("De mutatie is tegengeboekt.");
      await reload();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De mutatie kon niet worden tegengeboekt."),
      );
    } finally {
      setWorking(false);
    }
  };

  const overtimeType = leaveTypes.find((row) => row.category === "overtime");
  const firstReversible = new Set<string>();

  return (
    <>
      {canManage && (
        <div className="balance-mutation-tools">
          <form className="surface balance-adjustment-form" onSubmit={submitAdjustment}>
            <div className="balance-section-heading">
              <div>
                <h2>Uren op- of afboeken</h2>
                <p>
                  Gebruik een correctie alleen als een gewone verlof- of
                  overurenregistratie niet past.
                </p>
              </div>
            </div>
            <label>
              <span>Soort boeking</span>
              <select
                value={kind}
                onChange={(event) => {
                  const value = event.target.value as typeof kind;
                  setKind(value);
                  if (value === "payout" && overtimeType) setLeaveType(overtimeType.id);
                }}
              >
                <option value="opening">Startsaldo / overdracht</option>
                <option value="credit">Uren toevoegen</option>
                <option value="correction">Correctie</option>
                <option value="expiry">Uren laten vervallen</option>
                <option value="payout" disabled={!overtimeType}>
                  Overuren uitbetalen
                </option>
              </select>
            </label>
            {kind === "correction" && (
              <div
                className="segmented-control mutation-direction"
                aria-label="Correctierichting"
              >
                <button
                  type="button"
                  className={direction === "add" ? "active" : ""}
                  onClick={() => setDirection("add")}
                >
                  <Plus size={15} /> Bijboeken
                </button>
                <button
                  type="button"
                  className={direction === "subtract" ? "active" : ""}
                  onClick={() => setDirection("subtract")}
                >
                  <Banknote size={15} /> Afboeken
                </button>
              </div>
            )}
            <div className="form-row">
              <label>
                <span>Verloftype</span>
                <select
                  value={leaveType ?? ""}
                  onChange={(event) => setLeaveType(Number(event.target.value))}
                  disabled={kind === "payout"}
                >
                  {leaveTypes
                    .filter((row) => kind !== "payout" || row.category === "overtime")
                    .map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.name}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                  <span>Aantal {adjustmentUnit === "days" ? "dagen" : "uren"}</span>
                  <input
                    type="number"
                    min="0.25"
                  step="0.25"
                  value={hours}
                  onChange={(event) => setHours(event.target.value)}
                  required
                />
              </label>
              <label>
                <span>Eenheid</span>
                <select
                  value={adjustmentUnit}
                  onChange={(event) =>
                    setAdjustmentUnit(event.target.value as "hours" | "days")
                  }
                >
                  <option value="hours">Uren</option>
                  <option value="days">Dagen</option>
                </select>
              </label>
              {adjustmentUnit === "days" && (
                <label>
                  <span>Uren per dag</span>
                  <input
                    type="number"
                    min="0.25"
                    step="0.25"
                    value={dayHours}
                    onChange={(event) => setDayHours(event.target.value)}
                    required
                  />
                </label>
              )}
            </div>
            <div className="form-row">
              <label>
                <span>Boekingsdatum</span>
                <DateField
                  value={date}
                  onChange={(value) => setDate(value)}
                  required
                />
              </label>
              {(kind === "opening" ||
                kind === "credit" ||
                (kind === "correction" && direction === "add")) && (
                <label>
                  <span>Vervaldatum</span>
                  <DateField
                    min={date}
                    value={expiresOn}
                    onChange={(value) => setExpiresOn(value)}
                    required
                  />
                </label>
              )}
            </div>
            <label>
              <span>Omschrijving</span>
              <input
                value={description}
                maxLength={240}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Waarom wordt dit saldo aangepast?"
                required
              />
            </label>
            <button className="button primary" type="submit" disabled={working}>
              <Save size={17} /> Mutatie boeken
            </button>
          </form>

          <form className="surface carry-over-form" onSubmit={submitTransport}>
            <div className="balance-section-heading">
              <div>
                <h2>Overjarige uren</h2>
                <p>
                  Leg vast uit welk jaar de uren komen en wanneer het resterende saldo
                  vervalt.
                </p>
              </div>
            </div>
            <label>
              <span>Verloftype</span>
              <select
                value={transportType ?? ""}
                onChange={(event) => setTransportType(Number(event.target.value))}
              >
                {leaveTypes
                  .filter(
                    (row) => !["overtime", "time_for_time"].includes(row.category),
                  )
                  .map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.name}
                    </option>
                  ))}
              </select>
            </label>
            <div className="form-row">
              <label>
                <span>Bronjaar</span>
                <input
                  type="number"
                  min="2000"
                  max="2200"
                  value={sourceYear}
                  onChange={(event) => setSourceYear(event.target.value)}
                  required
                />
              </label>
              <label>
                <span>Aantal uren</span>
                <input
                  type="number"
                  min="0.25"
                  step="0.25"
                  value={transportHours}
                  onChange={(event) => setTransportHours(event.target.value)}
                  required
                />
              </label>
            </div>
            <label>
              <span>Vervaldatum</span>
              <DateField
                value={transportExpiry}
                onChange={(value) => setTransportExpiry(value)}
                required
              />
            </label>
            <button className="button subtle" type="submit" disabled={working}>
              <Plus size={17} /> Transport toevoegen
            </button>
          </form>
        </div>
      )}

      <section className="surface mutation-history">
        <div className="balance-section-heading">
          <div>
            <h2>Mutatiehistorie</h2>
            <p>
              Iedere toekenning, opname, correctie en tegenboeking blijft zichtbaar.
            </p>
          </div>
        </div>
        {mutations.length === 0 ? (
          <div className="empty-state compact">
            <History size={25} />
            <h2>Nog geen mutaties</h2>
            <p>Boekingen verschijnen hier zodra saldo wordt toegekend of gebruikt.</p>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Type</th>
                  <th>Boeking</th>
                  <th>Omschrijving</th>
                  <th>Voor</th>
                  <th>Na</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {mutations.map((mutation) => {
                  const showReverse =
                    mutation.can_reverse && !firstReversible.has(mutation.reference_id);
                  if (showReverse) firstReversible.add(mutation.reference_id);
                  return (
                    <tr key={mutation.id}>
                      <td>
                        {mutation.date
                          ? formatDate(mutation.date)
                          : formatDate(mutation.created_at.slice(0, 10))}
                      </td>
                      <td>{mutation.leave_type_name}</td>
                      <td>
                        <span
                          className={
                            mutation.minutes_delta < 0 ? "negative" : "positive"
                          }
                        >
                          {mutation.minutes_delta > 0 ? "+" : ""}
                          {formatMinutes(mutation.minutes_delta)}
                        </span>
                        <small>
                          {mutationReason(mutation.reason, mutation.source)}
                        </small>
                      </td>
                      <td>{mutation.description || "-"}</td>
                      <td>
                        {mutation.balance_before === null
                          ? "-"
                          : formatMinutes(mutation.balance_before)}
                      </td>
                      <td>
                        {mutation.balance_after === null
                          ? "-"
                          : formatMinutes(mutation.balance_after)}
                      </td>
                      <td>
                        {showReverse && canManage && (
                          <button
                            className="icon-button"
                            type="button"
                            title="Tegenboeken"
                            aria-label="Tegenboeken"
                            disabled={working}
                            onClick={() => void reverse(mutation)}
                          >
                            <RotateCcw size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function categoryLabel(category: string) {
  return (
    {
      leave: "Verlof",
      adv: "ATV",
      atv: "ATV",
      special: "Bijzonder verlof",
      overtime: "Overuren",
      time_for_time: "Tijd voor tijd",
      parental: "Ouderschapsverlof",
      unpaid: "Onbetaald verlof",
      other: "Overig",
    }[category] ?? category
  );
}

function mutationReason(reason: string, source: string) {
  if (source === "automatic_expiry") return "Automatisch vervallen";
  if (source === "manual_opening") return "Startsaldo / overdracht";
  if (source === "manual_expiry") return "Handmatig vervallen";
  if (source === "manual_payout") return "Uitbetaald";
  return (
    {
      approval: "Goedgekeurd",
      reversal: "Tegenboeking",
      manual: "Handmatige correctie",
      import: "Import",
      carry_over: "Transport",
      entitlement: "Toekenning",
      expiry: "Vervallen",
      payout: "Uitbetaald",
    }[reason] ?? reason
  );
}
